import React from 'react';
import SplineChartV2, { SplineChartV2Series } from 'src/components/BeaconV2Components/SplineChartV2/SplineChartV2';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { useMetricFormatter } from 'src/utils/Hooks';
import { useTimePeriods } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import LegendWithMetric from 'src/components/BeaconV2Components/Legend/LegendWithMetric';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';
import { useVariationsAnalyticStore } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/stores/variationsAnalyticStore';
import { VariationsFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/helpers/contentVariationUtils';
import { VariationsSplineAdapter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/VariationsSplineAdapter';
import { useDynamicVariationsSubtitles } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/useDynamicVariationsSubtitles';
import NoDataPlaceHolder, {
  NoDataIcon
} from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { NewBeaconFileIcon } from 'src/components/SvgIcons';
import { Box } from '@mui/material';

/**
 * Converts TimeSeriesPoint array [weekId, value][] to SplineChartV2Series format
 */
const convertToSplineV2Series = (
  data: [number, number][],
  id: string,
  name: string
): SplineChartV2Series => ({
  id,
  name,
  data: data.map(([weekId, value]) => ({ weekId, value }))
});

const FILTER_CONFIG = {
  [VariationsFilter.ChildVariationRate]: {
    title: 'Child Variation Rate',
    metricType: METRICTYPE.PERCENT
  },
  [VariationsFilter.SingleSkuPages]: {
    title: 'Unvariated Child SKUs',
    metricType: METRICTYPE.VOLUME
  },
  [VariationsFilter.VariatedSkuPages]: {
    title: 'Variated Child SKUs',
    metricType: METRICTYPE.VOLUME
  },
  [VariationsFilter.ChildAccuracy]: {
    title: 'Child SKU Accuracy',
    metricType: METRICTYPE.PERCENT
  },
  [VariationsFilter.IncorrectChildSkus]: {
    title: 'Inaccurate Child SKUs',
    metricType: METRICTYPE.VOLUME
  },
  [VariationsFilter.CorrectChildSkus]: {
    title: 'Accurate Child SKUs',
    metricType: METRICTYPE.VOLUME
  },
  [VariationsFilter.ParentPageAccuracy]: {
    title: 'Parent Page Accuracy',
    metricType: METRICTYPE.PERCENT
  },
  [VariationsFilter.InaccurateParentPages]: {
    title: 'Inaccurate Parent Pages',
    metricType: METRICTYPE.VOLUME
  },
  [VariationsFilter.PageDensity]: {
    title: 'Page Density',
    metricType: METRICTYPE.PERCENT
  },
  [VariationsFilter.ParentPageCount]: {
    title: 'Parent Page Count',
    metricType: METRICTYPE.VOLUME
  }
};

interface VariationsSplineChartProps {
  metricType?: METRICTYPE;
  queryFn: (filterKey: VariationsFilter) => Promise<VariationsSplineAdapter>;
}

export const VariationsSplineChart = (props: VariationsSplineChartProps) => {
  const { metricType = METRICTYPE.PERCENT, queryFn } = props;
  const { variationsFilter } = useVariationsAnalyticStore();
  const { mainTimePeriod, comparisonTimePeriod } = useTimePeriods();
  const { generateVariationsSplineSubtitle } = useDynamicVariationsSubtitles();
  const formatMetric = useMetricFormatter();

  const currentConfig = FILTER_CONFIG[variationsFilter] || FILTER_CONFIG[VariationsFilter.ChildVariationRate];

  return (
    <BeaconProChart
      queryFn={() => queryFn(variationsFilter)}
      name={`variations-spline-chart-${variationsFilter
        .toLowerCase()
        .replace(/\s+/g, '-')}-filtered-${variationsFilter}`}
    >
      <BeaconProChart.Header>
        <BeaconProChart.Title>
          <Text variant="h4">{currentConfig.title}</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(data: VariationsSplineAdapter) => {
          const metricName = currentConfig.title.toLowerCase();
          if (!data) {
            return generateVariationsSplineSubtitle(metricName);
          }

          const transformedData = data.transform();
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = transformedData?.legendData || {};

          return generateVariationsSplineSubtitle(
            metricName,
            primaryLegendMetric,
            percentChange,
            currentConfig.metricType
          );
        }}
      />

      <BeaconProChart.Legend
        render={(data: VariationsSplineAdapter) => {
          const transformedData = data.transform();
          const { primaryLegendMetric, secondaryLegendMetric, percentChange } = transformedData?.legendData || {};

          return (
            <Flex gap="lg" sx={{ marginTop: '32px' }}>
              <LegendWithMetric
                color="casper"
                displayName={comparisonTimePeriod.displayName}
                metric={formatMetric(secondaryLegendMetric || 0, currentConfig.metricType, { decimalPlaces: 2 })}
              />
              <LegendWithMetric
                color="primary"
                displayName={mainTimePeriod.displayName}
                metric={formatMetric(primaryLegendMetric || 0, currentConfig.metricType, { decimalPlaces: 2 })}
                change={formatMetric(Math.abs(percentChange || 0), METRICTYPE.PERCENT, { decimalPlaces: 1 })}
                direction={getDirection(percentChange || 0)}
              />
            </Flex>
          );
        }}
      />
      <BeaconProChart.Visualization
        render={(data: VariationsSplineAdapter, isLoading: boolean) => {
          const transformedData = data.transform();
          const { primaryMetrics, secondaryMetrics } = transformedData?.chartData || {};

          const hasNoData =
            !isLoading &&
            (!primaryMetrics || primaryMetrics.length === 0) &&
            (!secondaryMetrics || secondaryMetrics.length === 0);

          if (hasNoData) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '200px',
                  marginTop: '32px'
                }}
              >
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  icon={<NoDataIcon icon={<NewBeaconFileIcon />} />}
                />
              </Box>
            );
          }

          const primarySeries = convertToSplineV2Series(primaryMetrics || [], 'primary', 'Primary');
          const comparisonSeries = convertToSplineV2Series(secondaryMetrics || [], 'comparison', 'Comparison');

          return (
            <div style={{ marginTop: '32px' }}>
              <SplineChartV2
                primarySeries={primarySeries}
                comparisonSeries={comparisonSeries}
                metricType={currentConfig.metricType}
              />
            </div>
          );
        }}
      />
    </BeaconProChart>
  );
};
